import { defineExperiment } from '@learnforge/demo-render/harness';
import type {
  ExperimentSpec,
  PanelRow,
  ActionBarSpec,
  HarnessController,
} from '@learnforge/demo-render/harness';
import type {
  FigureElement,
  FigureHandle,
  MarksHandle,
  ReadoutHandle,
  ReadoutRow,
  MarkSpec,
  GridHandle,
} from '@learnforge/demo-render';
import type { GeometryContext } from '../types';
import type { GeometryKey } from '../locales/keys';
import type { Vec2 } from '@learnforge/demo-math/geometry';
import { dist, perigalPieces, realizeTriangle } from '@learnforge/demo-math/geometry';
import { formatNumber } from '@learnforge/demo-math/numeric';
import type { DissectionHandle } from '@learnforge/demo-render/geometry';
import { PYTHAGORAS_PRESETS } from '../data/pythagoras-presets';
import type { PythagorasMode, ShapeKind } from '../data/pythagoras-presets';

// ============================================================================
// Pure Helper Functions (exported and tested)
// ============================================================================

/**
 * Check if a² + b² is a perfect square.
 * Returns c (integer) if so, null otherwise.
 */
export function tripleCheck(a: number, b: number): number | null {
  const cSq = a * a + b * b;
  const c = Math.round(Math.sqrt(cSq));
  if (Math.abs(c * c - cSq) < 1e-9) {
    return c;
  }
  return null;
}

/**
 * Compute areas of the similar shape drawn on each side.
 * - square: s²
 * - semicircle: (π/8)·s²
 * - equilateral: (√3/4)·s²
 */
export function similarShapeAreas(
  a: number,
  b: number,
  c: number,
  shape: ShapeKind,
): { A: number; B: number; C: number } {
  const factor = (s: number): number => {
    switch (shape) {
      case 'square':
        return s * s;
      case 'semicircle':
        return (Math.PI / 8) * s * s;
      case 'equilateral':
        return (Math.sqrt(3) / 4) * s * s;
    }
  };
  return { A: factor(a), B: factor(b), C: factor(c) };
}

export interface SquareEntry {
  side: 'a' | 'b' | 'c';
  area: number;
}

export interface SquaresModel {
  squares: SquareEntry[];
  readoutRows: ReadoutRow[];
  dissectionPlan: ReturnType<typeof perigalPieces>;
}

/**
 * Build the squares-mode display model for given legs a, b.
 */
export function buildSquaresModel(a: number, b: number): SquaresModel {
  const c = Math.sqrt(a * a + b * b);
  const aArea = a * a;
  const bArea = b * b;
  const cArea = c * c;

  const squares: SquareEntry[] = [
    { side: 'a', area: aArea },
    { side: 'b', area: bArea },
    { side: 'c', area: cArea },
  ];

  const fmt = (n: number): string => {
    const rounded = Math.round(n * 100) / 100;
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
  };

  const readoutRows: ReadoutRow[] = [
    { id: 'a2', lhs: 'a²', relation: '=', rhs: fmt(aArea) },
    { id: 'b2', lhs: 'b²', relation: '=', rhs: fmt(bArea) },
    { id: 'c2', lhs: 'c²', relation: '=', rhs: fmt(cArea) },
    {
      id: 'sum',
      lhs: `${fmt(aArea)} + ${fmt(bArea)}`,
      relation: '=',
      rhs: fmt(cArea),
      emphasis: 'invariant',
    },
  ];

  return { squares, readoutRows, dissectionPlan: perigalPieces(a, b) };
}

export interface ConverseResult {
  comparison: '<' | '=' | '>';
  emphasis: 'invariant' | 'broken';
  lhsValue: number;
  rhsValue: number;
}

/**
 * Build the converse model: compare a²+b² with c².
 * Returns the comparison symbol and emphasis class.
 */
export function buildConverseModel(a: number, b: number, c: number): ConverseResult {
  const lhs = a * a + b * b;
  const rhs = c * c;
  const eps = 1e-9;
  let comparison: '<' | '=' | '>';
  if (Math.abs(lhs - rhs) < eps) {
    comparison = '=';
  } else if (lhs > rhs) {
    comparison = '>';
  } else {
    comparison = '<';
  }
  const emphasis = comparison === '=' ? 'invariant' : 'broken';
  return { comparison, emphasis, lhsValue: lhs, rhsValue: rhs };
}

export interface CanonicalTriple {
  primitive: [number, number, number];
  scale: number;
}

/**
 * Reduce (a, b) to canonical form if a²+b² is a perfect square.
 * Finds the GCD of a and b, divides, checks if the primitive is a Pythagorean triple.
 * Returns { primitive: [pa, pb, pc], scale } or null if not a triple.
 */
export function reduceTripleToCanonical(a: number, b: number): CanonicalTriple | null {
  const c = tripleCheck(a, b);
  if (c === null) return null;

  // Find GCD of the integer legs to reduce to primitive
  const gcd = (x: number, y: number): number => (y === 0 ? x : gcd(y, x % y));
  const g = gcd(Math.round(a), Math.round(b));
  const pa = Math.round(a) / g;
  const pb = Math.round(b) / g;
  const pc = tripleCheck(pa, pb);
  if (pc === null) return null;

  // Order so pa <= pb
  const [lo, hi] = pa <= pb ? [pa, pb] : [pb, pa];
  return { primitive: [lo, hi, pc], scale: g };
}

// ============================================================================
// Internal Geometry Helpers
// ============================================================================

const STAGE_W = 800;
const STAGE_H = 460;

/** Grid unit size in screen pixels */
const GRID_UNIT = 30;

/** Default legs in world units */
const DEFAULT_LEG_A = 5;
const DEFAULT_LEG_B = 5;

/** World coords for the right-angle vertex from the bottom-left */
const ORIGIN_X_WORLD = 2;
const ORIGIN_Y_WORLD = 2;

/** Max world coords given stage size and grid unit */
const WORLD_H = STAGE_H / GRID_UNIT;

/** Squares-mode layout: margin, scale cap, and largest draggable leg. */
const SQUARES_MARGIN = 40;
const SQUARES_MAX_SCALE = 30;
const MAX_LEG = 13;

interface SquaresLayout {
  /** Screen px per world unit. */
  S: number;
  /** Right-angle vertex. */
  O: Vec2;
  /** End of the horizontal leg a. */
  H: Vec2;
  /** End of the vertical leg b. */
  V: Vec2;
}

/**
 * Compute a self-contained layout for squares mode that draws the three OUTWARD
 * squares fully inside the stage and centred:
 *   - leg-a square below the horizontal leg,
 *   - leg-b square left of the vertical leg,
 *   - hypotenuse square on the outer side of H–V.
 * The bounding box (world units, relative to O) is x ∈ [−b, a+b], y ∈ [−(a+b), a],
 * so width = a+2b and height = 2a+b. The scale auto-fits that box (capped so small
 * triangles aren't blown up), and O is placed to centre the box in the stage.
 */
function squaresLayout(legA: number, legB: number): SquaresLayout {
  const figW = legA + 2 * legB;
  const figH = 2 * legA + legB;
  const autoFit = Math.min(
    (STAGE_W - 2 * SQUARES_MARGIN) / figW,
    (STAGE_H - 2 * SQUARES_MARGIN) / figH,
  );
  const S = Math.min(SQUARES_MAX_SCALE, autoFit);
  const O: Vec2 = {
    x: STAGE_W / 2 - (legA / 2) * S,
    y: STAGE_H / 2 + (legB / 2) * S,
  };
  const H: Vec2 = { x: O.x + legA * S, y: O.y };
  const V: Vec2 = { x: O.x, y: O.y - legB * S };
  return { S, O, H, V };
}

/**
 * Clamp leg to [1, max] in world units.
 */
function clampLeg(val: number, max: number): number {
  return Math.max(1, Math.min(max, Math.round(val)));
}

/**
 * Add a shape (polygon approximation) on a given side in figure elements.
 * Shape is drawn outward from the triangle.
 */
function addShapeOnSide(
  elems: FigureElement[],
  p1: Vec2,
  p2: Vec2,
  _sideLen: number,
  label: string,
  shape: ShapeKind,
): void {
  // Shapes are sized to the side they sit on, measured in SCREEN pixels (`len`),
  // so they stay flush with the triangle regardless of unit scaling. `_sideLen`
  // (world units) is kept for the call signature but not used for geometry.
  if (shape === 'square') {
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    const nx = -dy / len;
    const ny = dx / len;
    const p3 = { x: p2.x + nx * len, y: p2.y + ny * len };
    const p4 = { x: p1.x + nx * len, y: p1.y + ny * len };
    const id1 = `shp-${label}-p3`;
    const id2 = `shp-${label}-p4`;
    const id3 = `shp-${label}-p1a`;
    const id4 = `shp-${label}-p2a`;
    elems.push(
      { kind: 'point', id: id1, at: p3 },
      { kind: 'point', id: id2, at: p4 },
      { kind: 'point', id: id3, at: p1 },
      { kind: 'point', id: id4, at: p2 },
      {
        kind: 'polygon',
        id: `shp-poly-${label}`,
        vertices: [id3, id4, id1, id2],
        style: { className: `geo-shape-${label}` },
      },
    );
  } else if (shape === 'semicircle') {
    const cx = (p1.x + p2.x) / 2;
    const cy = (p1.y + p2.y) / 2;
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const r = (Math.sqrt(dx * dx + dy * dy) || 1) / 2;
    const baseAngle = Math.atan2(dy, dx);
    const N = 16;
    const ptIds: string[] = [];
    for (let i = 0; i <= N; i++) {
      const angle = baseAngle + (Math.PI * i) / N;
      const id = `shp-semi-${label}-${i}`;
      elems.push({
        kind: 'point',
        id,
        at: { x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) },
      });
      ptIds.push(id);
    }
    const p1Id = `shp-${label}-semi-p1`;
    const p2Id = `shp-${label}-semi-p2`;
    elems.push({ kind: 'point', id: p1Id, at: p1 }, { kind: 'point', id: p2Id, at: p2 });
    elems.push({
      kind: 'polygon',
      id: `shp-poly-${label}`,
      vertices: [p1Id, ...ptIds, p2Id],
      style: { className: `geo-shape-${label}` },
    });
  } else {
    // equilateral
    const cx = (p1.x + p2.x) / 2;
    const cy = (p1.y + p2.y) / 2;
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    const nx = -dy / len;
    const ny = dx / len;
    const h = (Math.sqrt(3) / 2) * len;
    const apex = { x: cx + nx * h, y: cy + ny * h };
    const p1Id = `shp-${label}-eq-p1`;
    const p2Id = `shp-${label}-eq-p2`;
    const apexId = `shp-${label}-eq-apex`;
    elems.push(
      { kind: 'point', id: p1Id, at: p1 },
      { kind: 'point', id: p2Id, at: p2 },
      { kind: 'point', id: apexId, at: apex },
      {
        kind: 'polygon',
        id: `shp-poly-${label}`,
        vertices: [p1Id, p2Id, apexId],
        style: { className: `geo-shape-${label}` },
      },
    );
  }
}

/**
 * Build figure elements for the squares mode.
 *
 * The triangle has its right angle at O, the horizontal leg a along O→H and the
 * vertical leg b along O→V (V is above O). Each square is drawn on the OUTWARD
 * side of its edge (away from the triangle interior), all sized in screen px via
 * the scale `S` so they stay flush with the legs:
 *   - leg-a square: below O–H,
 *   - leg-b square: left of O–V,
 *   - hypotenuse square: on the side of H–V away from O.
 */
function buildSquaresFigure(
  O: Vec2,
  H: Vec2,
  V: Vec2,
  legA: number,
  legB: number,
  S: number,
  showAreas: boolean,
): FigureElement[] {
  const c = Math.sqrt(legA * legA + legB * legB);
  const elements: FigureElement[] = [];

  elements.push(
    { kind: 'point', id: 'pt-O', at: O },
    { kind: 'point', id: 'pt-H', at: H, draggable: {} },
    { kind: 'point', id: 'pt-V', at: V, draggable: {} },
  );

  elements.push(
    {
      kind: 'segment',
      id: 'seg-OH',
      from: 'pt-O',
      to: 'pt-H',
      style: { className: 'geo-tri-side' },
    },
    {
      kind: 'segment',
      id: 'seg-OV',
      from: 'pt-O',
      to: 'pt-V',
      style: { className: 'geo-tri-side' },
    },
    {
      kind: 'segment',
      id: 'seg-HV',
      from: 'pt-H',
      to: 'pt-V',
      style: { className: 'geo-tri-hyp' },
    },
  );

  // Square corners are geometry-only anchors for the polygons — render them
  // invisibly (radius 0) so only the square outlines show, not stray dots.
  const anchorStyle = { radius: 0 } as const;

  // Square on leg a: BELOW O–H (outward, away from V which is above).
  const O_a = { x: O.x, y: O.y + legA * S };
  const H_a = { x: H.x, y: H.y + legA * S };
  elements.push(
    { kind: 'point', id: 'sq-a-bl', at: O_a, style: anchorStyle },
    { kind: 'point', id: 'sq-a-br', at: H_a, style: anchorStyle },
    {
      kind: 'polygon',
      id: 'poly-sq-a',
      vertices: ['pt-O', 'pt-H', 'sq-a-br', 'sq-a-bl'],
      style: { className: 'geo-square-a' },
    },
  );

  // Square on leg b: LEFT of O–V (outward, away from H which is to the right).
  const O_b = { x: O.x - legB * S, y: O.y };
  const V_b = { x: V.x - legB * S, y: V.y };
  elements.push(
    { kind: 'point', id: 'sq-b-tl', at: O_b, style: anchorStyle },
    { kind: 'point', id: 'sq-b-bl', at: V_b, style: anchorStyle },
    {
      kind: 'polygon',
      id: 'poly-sq-b',
      vertices: ['pt-O', 'sq-b-tl', 'sq-b-bl', 'pt-V'],
      style: { className: 'geo-square-b' },
    },
  );

  // Square on hypotenuse: outward normal (away from O) is (legB, −legA)·S, whose
  // length already equals the hypotenuse screen length c·S, so it is the square's
  // own side vector.
  const outX = legB * S;
  const outY = -legA * S;
  const H2 = { x: H.x + outX, y: H.y + outY };
  const V2 = { x: V.x + outX, y: V.y + outY };
  elements.push(
    { kind: 'point', id: 'sq-c-h2', at: H2, style: anchorStyle },
    { kind: 'point', id: 'sq-c-v2', at: V2, style: anchorStyle },
    {
      kind: 'polygon',
      id: 'poly-sq-c',
      vertices: ['pt-H', 'sq-c-h2', 'sq-c-v2', 'pt-V'],
      style: { className: 'geo-square-c' },
    },
  );

  if (showAreas) {
    const centerA = {
      x: (O.x + H.x + H_a.x + O_a.x) / 4,
      y: (O.y + H.y + H_a.y + O_a.y) / 4,
    };
    const centerB = {
      x: (O.x + O_b.x + V_b.x + V.x) / 4,
      y: (O.y + O_b.y + V_b.y + V.y) / 4,
    };
    const centerC = {
      x: (H.x + H2.x + V2.x + V.x) / 4,
      y: (H.y + H2.y + V2.y + V.y) / 4,
    };
    const fmtArea = (n: number): string => {
      const r = Math.round(n * 10) / 10;
      return Number.isInteger(r) ? `${r}` : r.toFixed(1);
    };
    elements.push(
      { kind: 'point', id: 'lbl-a-anchor', at: centerA, style: anchorStyle },
      {
        kind: 'label',
        id: 'lbl-sq-a',
        anchor: 'lbl-a-anchor',
        offset: { x: 0, y: 0 },
        text: fmtArea(legA * legA),
        textAnchor: 'middle',
      },
      { kind: 'point', id: 'lbl-b-anchor', at: centerB, style: anchorStyle },
      {
        kind: 'label',
        id: 'lbl-sq-b',
        anchor: 'lbl-b-anchor',
        offset: { x: 0, y: 0 },
        text: fmtArea(legB * legB),
        textAnchor: 'middle',
      },
      { kind: 'point', id: 'lbl-c-anchor', at: centerC, style: anchorStyle },
      {
        kind: 'label',
        id: 'lbl-sq-c',
        anchor: 'lbl-c-anchor',
        offset: { x: 0, y: 0 },
        text: fmtArea(c * c),
        textAnchor: 'middle',
      },
    );
  }

  return elements;
}

/**
 * Build figure elements for converse mode.
 */
function buildConverseFigure(O: Vec2, H: Vec2, apex: Vec2): FigureElement[] {
  return [
    { kind: 'point', id: 'pt-O', at: O },
    { kind: 'point', id: 'pt-H', at: H },
    { kind: 'point', id: 'pt-apex', at: apex, draggable: {} },
    {
      kind: 'segment',
      id: 'seg-OH',
      from: 'pt-O',
      to: 'pt-H',
      style: { className: 'geo-tri-side' },
    },
    {
      kind: 'segment',
      id: 'seg-O-apex',
      from: 'pt-O',
      to: 'pt-apex',
      style: { className: 'geo-tri-side' },
    },
    {
      kind: 'segment',
      id: 'seg-H-apex',
      from: 'pt-H',
      to: 'pt-apex',
      style: { className: 'geo-tri-hyp' },
    },
  ];
}

/** Build marks for converse mode: right-angle square when exactly right, arc otherwise */
function buildConverseMarks(O: Vec2, H: Vec2, apex: Vec2, comparison: '<' | '=' | '>'): MarkSpec[] {
  if (comparison === '=') {
    return [
      {
        kind: 'right-angle',
        id: 'mark-right',
        vertex: O,
        from: H,
        to: apex,
      },
    ];
  }
  return [
    {
      kind: 'angle',
      id: 'mark-apex-angle',
      vertex: O,
      from: H,
      to: apex,
      strokes: 1,
    },
  ];
}

// ============================================================================
// State shape
// ============================================================================

interface PythagorasState {
  mode: PythagorasMode;
  legA: number;
  legB: number;
  shape: ShapeKind;
  showAreas: boolean;
  scrubT: number;
  presetSides: [number, number, number] | null;
  apexScreen: Vec2 | null;
}

// ============================================================================
// Spec factory
// ============================================================================

function buildSpec(): ExperimentSpec<PythagorasState, GeometryKey, GeometryContext> {
  // Closure-stashed handles created in setup()
  let grid: GridHandle;
  let figure: FigureHandle;
  let marks: MarksHandle;
  let readout: ReadoutHandle;
  let controllerRef: HarnessController<PythagorasState>;

  // Dissection lifecycle
  let dissection: DissectionHandle | null = null;
  let lastPlanKey: string | null = null;

  // Triples view-side state (not in State)
  const foundTriples: Array<{ key: string; primitive: [number, number, number]; scale: number }> =
    [];
  let triplesStrip: HTMLDivElement | null = null;

  function defaultState(): PythagorasState {
    return {
      mode: 'squares',
      legA: DEFAULT_LEG_A,
      legB: DEFAULT_LEG_B,
      shape: 'square',
      showAreas: true,
      scrubT: 0,
      presetSides: null,
      apexScreen: null,
    };
  }

  function effectiveModeFor(state: PythagorasState, advanced: boolean): PythagorasMode {
    // Foundation-only mode is squares; advanced-only modes are converse/triples/shapes.
    // When advanced is off and state.mode is one of those, fall back to squares.
    const advancedModes: PythagorasMode[] = ['converse', 'triples', 'shapes'];
    return !advanced && advancedModes.includes(state.mode) ? 'squares' : state.mode;
  }

  function applyPresetLocal(
    state: PythagorasState,
    presetId: string,
    advanced: boolean,
  ): PythagorasState {
    const preset = PYTHAGORAS_PRESETS.find((p) => p.id === presetId);
    if (!preset || (preset.advancedOnly && !advanced)) return state;

    const next: PythagorasState = {
      ...state,
      mode: preset.mode,
      presetSides: null,
      apexScreen: null,
      scrubT: 0,
    };
    if (preset.legs) {
      next.legA = preset.legs[0];
      next.legB = preset.legs[1];
    }
    if (preset.sides) {
      next.presetSides = preset.sides;
    }
    return next;
  }

  function updateTriplesStrip(): void {
    if (!triplesStrip) return;
    triplesStrip.innerHTML = '';
    for (const triple of foundTriples) {
      const item = document.createElement('span');
      item.className = 'geo-triple-item';
      const [pa, pb, pc] = triple.primitive;
      if (triple.scale > 1) {
        item.textContent = `${triple.scale}×(${pa},${pb},${pc})`;
      } else {
        item.textContent = `(${pa},${pb},${pc})`;
      }
      triplesStrip.appendChild(item);
    }
  }

  // Coordinate helpers
  function getOriginScreen(grid: GridHandle): Vec2 {
    return grid.worldToScreen({ x: ORIGIN_X_WORLD, y: WORLD_H - ORIGIN_Y_WORLD });
  }

  function getHScreen(grid: GridHandle, legA: number): Vec2 {
    const o = getOriginScreen(grid);
    return { x: o.x + legA * GRID_UNIT, y: o.y };
  }

  function getVScreen(grid: GridHandle, legB: number): Vec2 {
    const o = getOriginScreen(grid);
    return { x: o.x, y: o.y - legB * GRID_UNIT };
  }

  return {
    labelKey: 'experiment.pythagoras',
    demoId: '2d-geometry',
    advancedKey: 'pythagoras',

    initialState(): PythagorasState {
      return defaultState();
    },

    applyPreset(state, presetId, ctx): PythagorasState {
      return applyPresetLocal(state, presetId, ctx.advanced);
    },

    setup(ctx, controller) {
      controllerRef = controller;
      grid = controller.createGrid({
        width: STAGE_W,
        height: STAGE_H,
        unit: GRID_UNIT,
        faint: true,
      });
      figure = controller.createFigure();
      marks = controller.createMarks();
      readout = controller.createReadout();

      // Triples HTML strip overlay in ctx.root
      const strip = document.createElement('div');
      strip.className = 'geo-triples-strip';
      ctx.root.appendChild(strip);
      triplesStrip = strip;

      // Register cleanup
      controller.registerCleanup(() => {
        if (dissection) {
          dissection.destroy();
          dissection = null;
        }
        lastPlanKey = null;
        foundTriples.length = 0;
        if (triplesStrip && triplesStrip.parentNode) {
          triplesStrip.parentNode.removeChild(triplesStrip);
        }
        triplesStrip = null;
      });
    },

    render(state: PythagorasState, ctx): void {
      const effectiveMode = effectiveModeFor(state, ctx.advanced);

      switch (effectiveMode) {
        case 'squares':
          renderSquaresMode(state);
          break;
        case 'converse':
          renderConverseMode(state);
          break;
        case 'triples':
          renderTriplesMode(state);
          break;
        case 'shapes':
          renderShapesMode(state);
          break;
      }

      function renderSquaresMode(state: PythagorasState): void {
        // Squares mode owns its scale/centre, so the lattice would not line up —
        // hide it and let the three squares carry the figure.
        grid.setVisible(false);

        const { S, O, H, V } = squaresLayout(state.legA, state.legB);

        figure.update(buildSquaresFigure(O, H, V, state.legA, state.legB, S, state.showAreas));

        marks.update([
          {
            kind: 'right-angle',
            id: 'mark-right',
            vertex: O,
            from: H,
            to: V,
          },
        ]);

        const model = buildSquaresModel(state.legA, state.legB);
        readout.update(model.readoutRows);

        // Dissection rebuild logic
        const planKey = `${state.legA},${state.legB}`;
        if (planKey !== lastPlanKey) {
          if (dissection) {
            dissection.destroy();
            dissection = null;
          }
          dissection = controllerRef.createDissection({
            plan: model.dissectionPlan,
            width: STAGE_W,
            height: STAGE_H,
          });
          lastPlanKey = planKey;
        }
        if (dissection) {
          // Map the Perigal target c-square ([0,0]→[c,c], y-down) onto the drawn
          // hypotenuse square: helper [0,0]→H and [c,0]→V, a translate+rotate+scale
          // similarity transform. At t=1 the pieces tile the drawn c-square.
          const deg = (Math.atan2(V.y - H.y, V.x - H.x) * 180) / Math.PI;
          dissection.group.setAttribute(
            'transform',
            `translate(${H.x} ${H.y}) rotate(${deg}) scale(${S})`,
          );
          // Only reveal the rearrangement while scrubbing; at rest the clean
          // three squares stand on their own.
          dissection.group.style.display = state.scrubT > 0.001 ? '' : 'none';
          dissection.setT(state.scrubT);
        }
      }

      function renderConverseMode(state: PythagorasState): void {
        grid.setVisible(true);
        if (dissection) {
          dissection.destroy();
          dissection = null;
        }
        lastPlanKey = null;

        const O = getOriginScreen(grid);
        const H = getHScreen(grid, state.legA);

        // Derive apex: if apexScreen is null, compute from presetSides or getVScreen
        let apex: Vec2;
        if (state.apexScreen === null) {
          if (state.presetSides !== null) {
            const tri = realizeTriangle({ kind: 'SSS', sides: state.presetSides });
            if (tri !== null) {
              apex = {
                x: O.x + tri.c.x * GRID_UNIT,
                y: O.y - tri.c.y * GRID_UNIT,
              };
            } else {
              const V = getVScreen(grid, state.legB);
              apex = V;
            }
          } else {
            const V = getVScreen(grid, state.legB);
            apex = V;
          }
        } else {
          apex = state.apexScreen;
        }

        figure.update(buildConverseFigure(O, H, apex));

        const a = dist(O, H) / GRID_UNIT;
        const b = dist(O, apex) / GRID_UNIT;
        const c = dist(H, apex) / GRID_UNIT;
        const { comparison, emphasis, lhsValue, rhsValue } = buildConverseModel(a, b, c);

        marks.update(buildConverseMarks(O, H, apex, comparison));

        const fmt = (n: number): string => {
          const r = Math.round(n * 10) / 10;
          return Number.isInteger(r) ? String(r) : r.toFixed(1);
        };
        const a2 = fmt(a * a);
        const b2 = fmt(b * b);
        const rows: ReadoutRow[] = [
          {
            id: 'conv-lhs',
            lhs: 'a²+b²',
            relation: comparison,
            rhs: 'c²',
            emphasis,
          },
          {
            id: 'conv-vals',
            lhs: `${a2} + ${b2} = ${fmt(lhsValue)}`,
            relation: comparison,
            rhs: fmt(rhsValue),
            emphasis,
          },
        ];
        readout.update(rows);
      }

      function renderTriplesMode(state: PythagorasState): void {
        grid.setVisible(false);
        if (dissection) {
          dissection.destroy();
          dissection = null;
        }
        lastPlanKey = null;

        const { S, O, H, V } = squaresLayout(state.legA, state.legB);
        const elems = buildSquaresFigure(O, H, V, state.legA, state.legB, S, state.showAreas);

        const c = tripleCheck(state.legA, state.legB);
        if (c !== null) {
          const hypIdx = elems.findIndex((e) => e.id === 'seg-HV');
          if (hypIdx >= 0) {
            const seg = elems[hypIdx]!;
            if (seg.kind === 'segment') {
              elems[hypIdx] = {
                ...seg,
                style: { className: 'geo-tri-hyp geo-triple-highlight' },
              };
            }
          }
          const reduced = reduceTripleToCanonical(state.legA, state.legB);
          if (reduced) {
            const key = `${reduced.primitive[0]},${reduced.primitive[1]},${reduced.primitive[2]}`;
            if (!foundTriples.some((t) => t.key === key)) {
              foundTriples.push({ key, ...reduced });
              updateTriplesStrip();
            }
          }
        }

        figure.update(elems);
        marks.update([
          {
            kind: 'right-angle',
            id: 'mark-right',
            vertex: O,
            from: H,
            to: V,
          },
        ]);

        const rows: ReadoutRow[] = [
          {
            id: 'triple-check',
            lhs: 'c',
            relation: c !== null ? '=' : '≈',
            rhs:
              c !== null
                ? String(c)
                : String(
                    Math.round(Math.sqrt(state.legA * state.legA + state.legB * state.legB) * 100) /
                      100,
                  ),
            emphasis: c !== null ? 'invariant' : 'neutral',
          },
          {
            id: 'triple-sum',
            lhs: `${state.legA}²+${state.legB}²`,
            relation: '=',
            rhs: String(state.legA * state.legA + state.legB * state.legB),
          },
        ];
        readout.update(rows);
      }

      function renderShapesMode(state: PythagorasState): void {
        grid.setVisible(true);
        if (dissection) {
          dissection.destroy();
          dissection = null;
        }
        lastPlanKey = null;

        const O = getOriginScreen(grid);
        const H = getHScreen(grid, state.legA);
        const V = getVScreen(grid, state.legB);

        const elems: FigureElement[] = [
          { kind: 'point', id: 'pt-O', at: O },
          { kind: 'point', id: 'pt-H', at: H, draggable: {} },
          { kind: 'point', id: 'pt-V', at: V, draggable: {} },
          {
            kind: 'segment',
            id: 'seg-OH',
            from: 'pt-O',
            to: 'pt-H',
            style: { className: 'geo-tri-side' },
          },
          {
            kind: 'segment',
            id: 'seg-OV',
            from: 'pt-O',
            to: 'pt-V',
            style: { className: 'geo-tri-side' },
          },
          {
            kind: 'segment',
            id: 'seg-HV',
            from: 'pt-H',
            to: 'pt-V',
            style: { className: 'geo-tri-hyp' },
          },
        ];

        const c = Math.sqrt(state.legA * state.legA + state.legB * state.legB);
        addShapeOnSide(elems, O, H, state.legA, 'a', state.shape);
        addShapeOnSide(elems, O, V, state.legB, 'b', state.shape);
        addShapeOnSide(elems, H, V, c, 'c', state.shape);

        figure.update(elems);
        marks.update([
          {
            kind: 'right-angle',
            id: 'mark-right',
            vertex: O,
            from: H,
            to: V,
          },
        ]);

        const { A, B, C } = similarShapeAreas(state.legA, state.legB, c, state.shape);
        const rows: ReadoutRow[] = [
          { id: 'shape-a', lhs: 'A(a)', relation: '=', rhs: formatNumber(A, { decimals: 2 }) },
          { id: 'shape-b', lhs: 'A(b)', relation: '=', rhs: formatNumber(B, { decimals: 2 }) },
          { id: 'shape-c', lhs: 'A(c)', relation: '=', rhs: formatNumber(C, { decimals: 2 }) },
          {
            id: 'shape-sum',
            lhs: `${formatNumber(A, { decimals: 2 })}+${formatNumber(B, { decimals: 2 })}`,
            relation: '=',
            rhs: formatNumber(C, { decimals: 2 }),
            emphasis: 'invariant',
          },
        ];
        readout.update(rows);
      }
    },

    panel(state: PythagorasState, ctx): PanelRow<GeometryKey, PythagorasState>[] {
      const effectiveMode = effectiveModeFor(state, ctx.advanced);

      const rows: PanelRow<GeometryKey, PythagorasState>[] = [
        {
          kind: 'dropdown',
          id: 'pythagoras-mode',
          labelKey: 'panel.mode',
          options: (() => {
            const opts: { value: string; labelKey: GeometryKey }[] = [
              { value: 'squares', labelKey: 'pythagoras.mode.squares' },
            ];
            if (ctx.advanced) {
              opts.push(
                { value: 'converse', labelKey: 'pythagoras.mode.converse' },
                { value: 'triples', labelKey: 'pythagoras.mode.triples' },
                { value: 'shapes', labelKey: 'pythagoras.mode.shapes' },
              );
            }
            return opts;
          })(),
          value: effectiveMode,
          onChange: (v) => ({
            ...state,
            mode: v as PythagorasMode,
            presetSides: null,
            apexScreen: null,
          }),
        },
        {
          kind: 'toggle',
          labelKey: 'pythagoras.panel.show-areas',
          value: state.showAreas,
          onChange: (v) => ({ ...state, showAreas: v }),
        },
      ];

      if (effectiveMode === 'shapes') {
        rows.push({
          kind: 'dropdown',
          id: 'pythagoras-shape',
          labelKey: 'pythagoras.panel.shape',
          options: [
            { value: 'square', labelKey: 'pythagoras.shape.square' },
            { value: 'semicircle', labelKey: 'pythagoras.shape.semicircle' },
            { value: 'equilateral', labelKey: 'pythagoras.shape.equilateral' },
          ],
          value: state.shape,
          onChange: (v) => ({ ...state, shape: v as ShapeKind }),
        });
      }

      rows.push({
        kind: 'advanced-toggle',
        labelKey: 'panel.advanced',
      });

      if (effectiveMode === 'squares') {
        rows.push({
          kind: 'slider',
          id: 'pythagoras-scrubber',
          labelKey: 'pythagoras.panel.scrubber',
          min: 0,
          max: 1,
          step: 0.01,
          value: state.scrubT,
          onChange: (v) => ({ ...state, scrubT: v }),
        });
      }

      return rows;
    },

    actions: (state: PythagorasState, ctx): ActionBarSpec<GeometryKey, PythagorasState> => {
      const effectiveMode = effectiveModeFor(state, ctx.advanced);
      return {
        onReset: () => defaultState(),
        onRandomise: (s) => {
          const a = clampLeg(1 + Math.floor(ctx.prng() * 11), 12);
          const b = clampLeg(1 + Math.floor(ctx.prng() * 11), 12);
          return { ...s, legA: a, legB: b, apexScreen: null };
        },
        presets: PYTHAGORAS_PRESETS.filter(
          (p) => p.mode === effectiveMode && (ctx.advanced || !p.advancedOnly),
        ).map((p) => ({ id: p.id, labelKey: p.labelKey })),
        onPreset: (s, id) => applyPresetLocal(s, id, ctx.advanced),
      };
    },

    onDrag(state: PythagorasState, pointId: string, at: Vec2): PythagorasState {
      if (pointId === 'pt-apex') {
        return { ...state, apexScreen: at };
      }

      // pt-H / pt-V appear in squares, triples (auto-fit layout) and shapes
      // (grid layout) — resolve the origin/scale the matching mode actually used.
      const effectiveMode = effectiveModeFor(state, controllerRef.advanced);
      const usesAutoFit = effectiveMode === 'squares' || effectiveMode === 'triples';
      const O = usesAutoFit ? squaresLayout(state.legA, state.legB).O : getOriginScreen(grid);
      const S = usesAutoFit ? squaresLayout(state.legA, state.legB).S : GRID_UNIT;

      if (pointId === 'pt-H') {
        const worldX = (at.x - O.x) / S;
        const snapped = clampLeg(worldX, MAX_LEG);
        if (snapped !== state.legA) {
          return { ...state, legA: snapped, apexScreen: null };
        }
      } else if (pointId === 'pt-V') {
        const worldY = (O.y - at.y) / S;
        const snapped = clampLeg(worldY, MAX_LEG);
        if (snapped !== state.legB) {
          return { ...state, legB: snapped, apexScreen: null };
        }
      }
      return state;
    },

    applyShotState(state: PythagorasState, _shotId: string): PythagorasState {
      return { ...state, mode: 'squares', scrubT: 0.5 };
    },
  };
}

export const experiment = defineExperiment(buildSpec());
